/** Oscar's "Stay in the loop!" marketing-consent panel: a checkbox with a
 * heading, a body (server statement + the fixed privacy sentence), and three
 * static benefit pills. Used on the new-user register step and the
 * existing-user consent step. Heading/body/default come from the consents
 * category (variant-driven); the pills are static design decoration.
 */

import type { MarketingPurpose } from "../api/types";
import { CONSENT_PRIVACY_LINK, consentBodyParts } from "../api/consent.copy";
import { ShoppingBagIcon, TentIcon, TicketIcon } from "./icons";
import { h } from "../lib/jsx";

const FALLBACK_TITLE = "Stay in the loop!";
// Only rendered when the category fetch fails, so it is the copy nobody sees
// until something is wrong — and correspondingly easy to leave stale. Kept in
// step with the opt-in variant in seekr's catalog.
const FALLBACK_STATEMENT =
  "Get the lowdown on new adventures, inspo from our award-winning magazine, " +
  "and ideas for where to go next. Tick the box for a dose of adventure in " +
  "your inbox – you can opt out whenever you like.";
// "Member-only departures" and "Exclusive discounts" claimed a membership tier
// and a discount programme, neither of which exists. The padlock went with the
// first: it read as the gated access being disclaimed.
const PILLS: Array<{ label: string; icon: () => Element }> = [
  { label: "First dibs on new trips", icon: TentIcon },
  { label: "Exclusive offers", icon: ShoppingBagIcon },
  { label: "Last minute deals", icon: TicketIcon },
];

export interface ConsentPanel {
  el: Element;
  box: HTMLInputElement;
  applyPurpose(purpose: MarketingPurpose | null): void;
}

export function consentPanel(opts: { privacyPolicyUrl: string }): ConsentPanel {
  const box = (<input type="checkbox" />) as HTMLInputElement;
  const titleEl = (<span class="otp-consent-title">{FALLBACK_TITLE}</span>) as HTMLElement;
  const leadEl = (<span />) as HTMLElement;
  const privacyPrefixEl = (<span />) as HTMLElement;
  const privacySuffixEl = (<span />) as HTMLElement;

  function render(statement: string): void {
    const parts = consentBodyParts(statement);
    leadEl.textContent = parts.lead ? parts.lead + " " : "";
    privacyPrefixEl.textContent = parts.privacyPrefix;
    privacySuffixEl.textContent = parts.privacySuffix;
  }
  render(FALLBACK_STATEMENT);

  const el = (
    <div class="otp-consent">
      <label class="otp-consent-head">
        {box} {titleEl}
      </label>
      <p class="otp-consent-body">
        {leadEl}
        {privacyPrefixEl}
        <a href={opts.privacyPolicyUrl} target="_blank" rel="noopener">
          {CONSENT_PRIVACY_LINK}
        </a>
        {privacySuffixEl}
      </p>
      <ul class="otp-consent-pills">
        {PILLS.map(({ label, icon }) => (
          <li class="otp-pill">{icon()} {label}</li>
        ))}
      </ul>
    </div>
  ) as Element;

  return {
    el,
    box,
    applyPurpose(purpose) {
      if (!purpose) return;
      if (purpose.title) titleEl.textContent = purpose.title;
      render(purpose.consent_statement ?? FALLBACK_STATEMENT);
      box.checked = !!purpose.granted_by_default;
    },
  };
}
